import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DateField,
  Icon,
  LinearProgress,
  Notice,
  PhotoGrid,
  SelectField,
  iconAdd,
} from '@sauda/ui/kit';
import { MAX_PHOTOS, PHOTO_ROOMS } from '@sauda/shared';
import { api } from '../../lib/api.js';
import { uploadPhotos, usablePhotos } from '../../lib/photo-upload.js';
import type { PhotoInfo } from '../../lib/types.js';

/** Photos of a saved property: add more, put them in order (the first is the cover), remove one. */
export function PhotoManager({
  propertyId,
  photos,
  onChanged,
}: {
  propertyId: string;
  photos: PhotoInfo[];
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement | null>(null);
  const [notice, setNotice] = useState<{ tone: 'info' | 'error' | 'success'; text: string } | null>(
    null,
  );
  const [progress, setProgress] = useState<number | null>(null);

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const { usable, skipped } = usablePhotos(files);
      if (skipped > 0) setNotice({ tone: 'info', text: t('photos.skipped') });
      if (usable.length === 0) return;
      setProgress(0);
      await uploadPhotos(propertyId, usable.slice(0, MAX_PHOTOS - photos.length), (done, total) =>
        setProgress((done / total) * 100),
      );
      setNotice({ tone: 'success', text: t('photos.processing') });
    },
    onError: () => setNotice({ tone: 'error', text: t('photos.failed') }),
    onSettled: () => {
      setProgress(null);
      if (input.current) input.current.value = '';
      onChanged();
    },
  });
  const order = useMutation({
    mutationFn: (ids: string[]) =>
      api(`/properties/${propertyId}/photos/order`, { method: 'PATCH', body: { ids } }),
    onSettled: onChanged,
  });
  const remove = useMutation({
    mutationFn: (photoId: string) =>
      api(`/properties/${propertyId}/photos/${photoId}`, { method: 'DELETE' }),
    onSettled: onChanged,
  });

  const tag = useMutation({
    mutationFn: (v: { id: string; room?: string | null; takenOn?: string | null }) =>
      api(`/properties/${propertyId}/photos/${v.id}/tag`, {
        method: 'PATCH',
        body: {
          ...(v.room === undefined ? {} : { room: v.room }),
          ...(v.takenOn === undefined ? {} : { takenOn: v.takenOn }),
        },
      }),
    onSettled: onChanged,
  });

  const move = (id: string, direction: -1 | 1) => {
    const ids = photos.map((p) => p.id);
    const index = ids.indexOf(id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target] as string, ids[index] as string];
    order.mutate(ids);
  };

  return (
    <div className="form-stack">
      <h2 className="k-t-title-sm">{t('photos.title')}</h2>
      {photos.length > 0 ? (
        <PhotoGrid
          label={t('photos.title')}
          photos={photos.map((p) => ({
            id: p.id,
            url: p.thumbUrl,
            status:
              p.status === 'ready' ? 'ready' : p.status === 'rejected' ? 'rejected' : 'processing',
            alt: t('photos.alt', { n: p.position }),
          }))}
          onMove={move}
          onRemove={(id) => remove.mutate(id)}
          labels={{
            earlier: t('photos.earlier'),
            later: t('photos.later'),
            remove: t('photos.remove'),
            cover: t('photos.cover'),
            processing: t('photos.status_pending'),
            rejected: t('photos.status_rejected'),
          }}
        />
      ) : (
        <p className="k-t-body k-muted">{t('photos.none')}</p>
      )}
      {photos.length === 0 ? null : (
        <ul className="photo-tags" aria-label={t('photos.tags')}>
          {photos.map((p) => (
            <li key={p.id} className="form-pair">
              <SelectField
                label={t('photos.room', { n: p.position })}
                value={p.room ?? ''}
                onChange={(e) => tag.mutate({ id: p.id, room: e.target.value || null })}
                options={[
                  { value: '', label: t('photos.noRoom') },
                  ...PHOTO_ROOMS.map((r) => ({ value: r, label: t(`photos.room_${r}`) })),
                ]}
              />
              <DateField
                label={t('photos.takenOn')}
                value={p.takenOn ?? ''}
                onChange={(v) => v !== '' && tag.mutate({ id: p.id, takenOn: v })}
              />
            </li>
          ))}
        </ul>
      )}
      <input
        ref={input}
        type="file"
        className="k-sr-only"
        accept="image/jpeg,image/png,image/webp"
        multiple
        aria-label={t('photos.choose')}
        onChange={(e) => upload.mutate(Array.from(e.target.files ?? []))}
      />
      {progress === null ? null : <LinearProgress label={t('photos.uploading')} value={progress} />}
      {notice === null ? null : <Notice tone={notice.tone}>{notice.text}</Notice>}
      <div>
        <Button
          variant="tonal"
          disabled={photos.length >= MAX_PHOTOS || upload.isPending}
          onClick={() => input.current?.click()}
        >
          <Icon icon={iconAdd} size={20} />
          {t('photos.add')}
        </Button>
      </div>
    </div>
  );
}
